import { useEffect, useCallback } from 'react';
import { X, Download } from 'lucide-react';
import { apiClient } from '@/api/client';
import { useSignedMediaUrl } from '@/hooks/useSignedMediaUrl';

interface ImageLightboxProps {
  mediaFileId: string;
  alt?: string;
  onClose: () => void;
}

export default function ImageLightbox({ mediaFileId, alt, onClose }: ImageLightboxProps) {
  const signedSrc = useSignedMediaUrl(mediaFileId, 'download');
  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [handleKey]);

  const handleDownload = async () => {
    try {
      const res = await apiClient.get(`/api/media/${mediaFileId}/download`, {
        responseType: 'blob',
      });
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = alt ?? 'image';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: open in new tab. Используем signed URL — он short-lived
      // (~5 мин TTL), не утекает в browser history как Bearer JWT.
      if (signedSrc) {
        window.open(signedSrc, '_blank');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/90" onClick={onClose} />

      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <button
          onClick={handleDownload}
          className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          title="Скачать"
        >
          <Download size={20} />
        </button>
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          title="Закрыть"
        >
          <X size={20} />
        </button>
      </div>

      {signedSrc ? (
        <img
          src={signedSrc}
          alt={alt ?? 'Изображение'}
          className="relative z-10 max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <div className="relative z-10 w-32 h-32 rounded-full border-4 border-white/20 border-t-white/80 animate-spin" />
      )}
    </div>
  );
}
